import React, { useEffect, useState } from "react";
import {
  Drawer,
  Tabs,
  List,
  Tag,
  Button,
  Space,
  Empty,
  notification,
  Spin,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SwapOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import Api from "api";
import styled from "styled-components";
import { ThemeConfig } from "theme";

const { Text, Paragraph } = Typography;

const StyledDrawer = styled(Drawer)`
  .ant-list-item {
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;
  }
  .change-old-name {
    text-decoration: line-through;
    color: #999;
  }
  .change-new-name {
    color: ${ThemeConfig.colors.primary};
    font-weight: 600;
  }
  .audit-note {
    font-size: 12px;
    color: #888;
    margin-top: 4px;
    font-style: italic;
  }
  .resolved-date {
    font-size: 11px;
    color: #aaa;
  }
`;

interface Props {
  isOpen: boolean;
  onClose: (action?: string) => void;
  collectionId: string;
}

const changeTypeColors: Record<string, string> = {
  rename: "blue",
  reclassify: "purple",
  remove: "red",
  merge: "orange",
  split: "cyan",
};

const statusColors: Record<string, string> = {
  pending: "warning",
  accepted: "success",
  rejected: "error",
};

const TaxonomicChangesDrawer = ({ isOpen, onClose, collectionId }: Props) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [allChanges, setAllChanges] = useState<any[]>([]);
  const [processing, setProcessing] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState<string>("pending");

  const fetchChanges = async () => {
    setLoading(true);
    try {
      const { data } = await Api.get(
        `/collections/${collectionId}/taxonomic-changes`,
        {}
      );
      if (data.success) {
        setAllChanges(data.data);
      }
    } catch (e: any) {
      console.error("Failed to fetch taxonomic changes", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen) {
      fetchChanges();
    }
  }, [isOpen]);

  const handleAccept = async (changeId: string) => {
    setProcessing((prev) => ({ ...prev, [changeId]: true }));
    try {
      const { data } = await Api.post(
        `/collections/${collectionId}/taxonomic-changes/${changeId}/accept`,
        {}
      );
      if (data.success) {
        notification.success({
          message: "Change Accepted",
          description: data.messages?.[0] || "Species reference updated.",
        });
        fetchChanges();
      }
    } catch (e: any) {
      notification.error({
        message: "Failed",
        description:
          e?.response?.data?.messages?.[0] || "Failed to accept change",
      });
    }
    setProcessing((prev) => ({ ...prev, [changeId]: false }));
  };

  const handleReject = async (changeId: string) => {
    setProcessing((prev) => ({ ...prev, [changeId]: true }));
    try {
      const { data } = await Api.post(
        `/collections/${collectionId}/taxonomic-changes/${changeId}/reject`,
        {}
      );
      if (data.success) {
        notification.success({
          message: "Change Rejected",
          description: data.messages?.[0] || "Original name kept.",
        });
        fetchChanges();
      }
    } catch (e: any) {
      notification.error({
        message: "Failed",
        description:
          e?.response?.data?.messages?.[0] || "Failed to reject change",
      });
    }
    setProcessing((prev) => ({ ...prev, [changeId]: false }));
  };

  const filteredChanges =
    activeTab === "all"
      ? allChanges
      : allChanges.filter((c) => c.status === activeTab);

  const pendingCount = allChanges.filter((c) => c.status === "pending").length;
  const acceptedCount = allChanges.filter(
    (c) => c.status === "accepted"
  ).length;
  const rejectedCount = allChanges.filter(
    (c) => c.status === "rejected"
  ).length;

  const renderChangeItem = (change: any) => {
    const isPending = change.status === "pending";

    return (
      <List.Item
        actions={
          isPending
            ? [
                <Button
                  key="accept"
                  type="primary"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  loading={processing[change._id]}
                  onClick={() => handleAccept(change._id)}
                >
                  Accept
                </Button>,
                <Button
                  key="reject"
                  size="small"
                  icon={<CloseCircleOutlined />}
                  loading={processing[change._id]}
                  onClick={() => handleReject(change._id)}
                >
                  Reject
                </Button>,
              ]
            : undefined
        }
      >
        <List.Item.Meta
          title={
            <Space size="small" wrap>
              <Tag
                color={changeTypeColors[change.change_type] || "default"}
              >
                {change.change_type}
              </Tag>
              {!isPending && (
                <Tag color={statusColors[change.status]}>
                  {change.status}
                </Tag>
              )}
              <Text delete={!!change.new_name} type="secondary">
                {change.old_name}
              </Text>
              {change.new_name && (
                <>
                  <SwapOutlined />
                  <Text strong style={{ color: ThemeConfig.colors.primary }}>
                    {change.new_name}
                  </Text>
                </>
              )}
            </Space>
          }
          description={
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                per {change.authority}
              </Text>
              {change.audit_note && (
                <Paragraph
                  className="audit-note"
                  style={{ marginBottom: 0, marginTop: 4 }}
                >
                  {change.audit_note}
                </Paragraph>
              )}
              {change.resolved_at && (
                <Text className="resolved-date">
                  Resolved:{" "}
                  {new Date(change.resolved_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </Text>
              )}
            </div>
          }
        />
      </List.Item>
    );
  };

  const tabItems = [
    {
      key: "pending",
      label: `Pending (${pendingCount})`,
      children: loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : filteredChanges.length === 0 ? (
        <Empty description="No pending changes" />
      ) : (
        <List dataSource={filteredChanges} renderItem={renderChangeItem} />
      ),
    },
    {
      key: "accepted",
      label: `Accepted (${acceptedCount})`,
      children: loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : filteredChanges.length === 0 ? (
        <Empty description="No accepted changes" />
      ) : (
        <List dataSource={filteredChanges} renderItem={renderChangeItem} />
      ),
    },
    {
      key: "rejected",
      label: `Rejected (${rejectedCount})`,
      children: loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : filteredChanges.length === 0 ? (
        <Empty description="No rejected changes" />
      ) : (
        <List dataSource={filteredChanges} renderItem={renderChangeItem} />
      ),
    },
    {
      key: "all",
      label: `All (${allChanges.length})`,
      children: loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : allChanges.length === 0 ? (
        <Empty description="No taxonomic changes" />
      ) : (
        <List dataSource={filteredChanges} renderItem={renderChangeItem} />
      ),
    },
  ];

  return (
    <StyledDrawer
      title={
        <Space>
          <HistoryOutlined />
          <span>Taxonomic Changes</span>
        </Space>
      }
      placement="right"
      width={520}
      open={isOpen}
      onClose={() => {
        const hadPending = pendingCount > 0;
        onClose(hadPending ? "refetch" : undefined);
      }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />
    </StyledDrawer>
  );
};

export default TaxonomicChangesDrawer;
